import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, MagnifyingGlass, Trash, Package, Truck } from '@phosphor-icons/react';

export default function PurchasePage() {
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Purchase Management</h1>
      <Tabs defaultValue="purchases">
        <TabsList>
          <TabsTrigger value="purchases">Purchase Orders</TabsTrigger>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
        </TabsList>
        <TabsContent value="purchases"><PurchaseList /></TabsContent>
        <TabsContent value="vendors"><VendorList /></TabsContent>
      </Tabs>
    </div>
  );
}

function PurchaseList() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => { api.getPurchases().then(r => { setPurchases(r.data.purchases || []); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="h-9 px-4 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={16} className="mr-1" /> New Purchase
        </Button>
      </div>

      <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <thead><tr style={{ background: '#F9F9FA' }}>
            {['PO Number', 'Date', 'Vendor', 'Items', 'Net Amount', 'Status'].map(h => (
              <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</td></tr> :
              purchases.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>No purchases yet</td></tr> :
              purchases.map((p, i) => (
                <tr key={i} className="hover:bg-slate-50" style={{ borderBottom: '1px solid #E2E4E9' }}>
                  <td className="px-4 py-2 font-medium" style={{ color: '#0A2540' }}>{p.purchase_number}</td>
                  <td className="px-4 py-2 text-xs" style={{ color: '#8A94A6' }}>{p.date}</td>
                  <td className="px-4 py-2">{p.vendor_name}</td>
                  <td className="px-4 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{p.total_items}</td>
                  <td className="px-4 py-2 font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{(p.net_amount || 0).toLocaleString()}</td>
                  <td className="px-4 py-2"><span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: p.payment_status === 'paid' ? '#F0FFF4' : '#FFF8E1', color: p.payment_status === 'paid' ? '#34C759' : '#E6A800' }}>{p.payment_status}</span></td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Purchase Order</DialogTitle></DialogHeader>
          <PurchaseForm onSave={() => { setShowForm(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PurchaseForm({ onSave }) {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [medSearch, setMedSearch] = useState('');
  const [medicines, setMedicines] = useState([]);
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [showMedDropdown, setShowMedDropdown] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.getVendors().then(r => setVendors(r.data)); }, []);
  useEffect(() => {
    if (medSearch.length >= 1) { api.getMedicines(medSearch).then(r => { setMedicines(r.data); setShowMedDropdown(true); }); }
    else { setMedicines([]); setShowMedDropdown(false); }
  }, [medSearch]);

  const addItem = (med) => {
    setItems([...items, { medicine_id: med.id, medicine_name: med.name, qty: 1, rate: med.dealer_price || 0, gst_percent: med.gst_percent || 5, amount: med.dealer_price || 0 }]);
    setMedSearch(''); setShowMedDropdown(false);
  };
  const updateItem = (idx, field, value) => {
    setItems(items.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (['qty', 'rate'].includes(field)) updated.amount = Number(updated.qty) * Number(updated.rate);
      return updated;
    }));
  };

  const grossAmount = items.reduce((s, i) => s + i.amount, 0);
  const totalGst = items.reduce((s, i) => s + (i.amount * i.gst_percent / 100), 0);
  const netAmount = Math.round(grossAmount + totalGst);

  const save = async () => {
    if (!selectedVendor || items.length === 0) return;
    setSaving(true);
    try {
      await api.createPurchase({ date, vendor_id: selectedVendor._id || selectedVendor.id, vendor_name: selectedVendor.name, items, gross_amount: grossAmount, total_gst: Math.round(totalGst * 100) / 100, net_amount: netAmount, total_discount: 0 });
      onSave();
    } catch {}
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Date</Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-8 mt-1" /></div>
        <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Vendor</Label>
          <select className="w-full h-8 mt-1 border rounded px-2 text-sm" style={{ borderColor: '#E2E4E9' }} onChange={e => setSelectedVendor(vendors.find(v => (v._id || v.id) === e.target.value))}>
            <option value="">Select vendor</option>
            {vendors.map(v => <option key={v._id || v.id} value={v._id || v.id}>{v.name}</option>)}
          </select></div>
      </div>
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8A94A6' }} />
        <Input value={medSearch} onChange={e => setMedSearch(e.target.value)} placeholder="Search medicine to add..." className="pl-9 h-8" />
        {showMedDropdown && medicines.length > 0 && (
          <div className="absolute z-20 mt-1 w-full border rounded shadow-lg max-h-40 overflow-y-auto" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            {medicines.map(m => (
              <button key={m.id} onClick={() => addItem(m)} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50" style={{ borderBottom: '1px solid #E2E4E9' }}>
                {m.name} <span style={{ color: '#8A94A6' }}>| Dealer: ₹{m.dealer_price}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {items.length > 0 && (
        <div className="rounded-md border overflow-hidden" style={{ borderColor: '#E2E4E9' }}>
          <table className="w-full text-xs">
            <thead><tr style={{ background: '#0A2540' }}>
              {['Medicine', 'Qty', 'Rate', 'GST%', 'Amount', ''].map(h => <th key={h} className="px-2 py-1.5 text-left text-white font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #E2E4E9' }}>
                  <td className="px-2 py-1 font-medium">{item.medicine_name}</td>
                  <td className="px-2 py-1"><Input type="number" value={item.qty} onChange={e => updateItem(idx, 'qty', Number(e.target.value))} className="h-6 w-14 text-xs" /></td>
                  <td className="px-2 py-1"><Input type="number" value={item.rate} onChange={e => updateItem(idx, 'rate', Number(e.target.value))} className="h-6 w-16 text-xs" /></td>
                  <td className="px-2 py-1 text-right">{item.gst_percent}%</td>
                  <td className="px-2 py-1 text-right font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{item.amount.toFixed(2)}</td>
                  <td className="px-2 py-1"><button onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash size={12} style={{ color: '#FF3B30' }} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-between items-center p-3 rounded-md" style={{ background: '#F0F4F8' }}>
        <span className="text-xs" style={{ color: '#8A94A6' }}>Gross: ₹{grossAmount.toFixed(2)} | GST: ₹{totalGst.toFixed(2)}</span>
        <span className="text-sm font-bold" style={{ color: '#0A2540' }}>Net: ₹{netAmount}</span>
      </div>
      <Button onClick={save} disabled={saving || !selectedVendor || items.length === 0} className="w-full h-9 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>
        {saving ? 'Saving...' : 'Create Purchase Order'}
      </Button>
    </div>
  );
}

function VendorList() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => { api.getVendors().then(r => { setVendors(r.data); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="h-9 px-4 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={16} className="mr-1" /> Add Vendor
        </Button>
      </div>
      <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <table className="w-full text-sm">
          <thead><tr style={{ background: '#F9F9FA' }}>
            {['Vendor', 'Phone', 'GSTIN', 'Total Purchases', 'Actions'].map(h => (
              <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</td></tr> :
              vendors.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>No vendors yet</td></tr> :
              vendors.map((v, i) => (
                <tr key={i} className="hover:bg-slate-50" style={{ borderBottom: '1px solid #E2E4E9' }}>
                  <td className="px-4 py-2 font-medium" style={{ color: '#0A2540' }}>{v.name}</td>
                  <td className="px-4 py-2 text-xs" style={{ color: '#8A94A6' }}>{v.phone}</td>
                  <td className="px-4 py-2 text-xs">{v.gstin}</td>
                  <td className="px-4 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{(v.total_purchases || 0).toLocaleString()}</td>
                  <td className="px-4 py-2"><button onClick={async () => { if (window.confirm('Delete?')) { await api.deleteVendor(v._id); load(); }}} className="p-1 rounded hover:bg-red-50"><Trash size={14} style={{ color: '#FF3B30' }} /></button></td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Vendor</DialogTitle></DialogHeader>
          <VendorForm onSave={() => { setShowForm(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VendorForm({ onSave }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', gstin: '', dl_number: '' });
  const save = async () => { try { await api.createVendor(form); onSave(); } catch {} };
  return (
    <div className="space-y-3">
      {[{ k: 'name', l: 'Vendor Name' }, { k: 'phone', l: 'Phone' }, { k: 'email', l: 'Email' }, { k: 'address', l: 'Address' }, { k: 'gstin', l: 'GSTIN' }, { k: 'dl_number', l: 'DL Number' }].map(f => (
        <div key={f.k}><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{f.l}</Label>
          <Input value={form[f.k]} onChange={e => setForm(fo => ({ ...fo, [f.k]: e.target.value }))} className="h-8 mt-1" /></div>
      ))}
      <Button onClick={save} className="w-full h-9 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>Save Vendor</Button>
    </div>
  );
}
